"use client"

import { useState, useEffect } from "react"
import { useApp } from "../../contexts/AppContext"
import { BiPlus, BiPencil, BiTrash } from "react-icons/bi"
import { Link } from "react-router-dom"
import { motion } from "framer-motion"

import ConfirmAlert from "../alerts/ConfirmAlert"
import TableLayout from "../tables/TableLayout"

export default function FeuilleTable({ feuilleId, classeurId, feuilleData, setFeuillesData }) {
  const {
    getFeuilleLignes,
    deleteFeuilleDataAPI,
    validateFeuilleDataAPI,
    getFeuille,
    loading,
  } = useApp()

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showValideConfirm, setShowValideConfirm] = useState(false)
  const [deleteDataId, setdeleteDataId] = useState(null)
  const [validateDataId, setValidateDataId] = useState(null)
  const [selectedItem, setSelectedItem] = useState([])
  const [feuilleColumns, setFeuilleColumns] = useState([])
  const currentFeuille = getFeuille(feuilleId)

  useEffect(() => {
    let isActive = true
    if (feuilleId) {
      const cols = getFeuilleLignes(feuilleId)
      setFeuilleColumns(prev => { return JSON.stringify(prev) !== JSON.stringify(cols) ? cols : prev })
    }
    return () => { isActive = false }
  }, [feuilleId])

  const handleDeleteData = async () => {
    if (deleteDataId) {
      try {
        const deleteData = { "code_feuille": feuilleId, "ids": [deleteDataId] }
        await deleteFeuilleDataAPI(deleteData)
        setShowDeleteConfirm(false)
        setdeleteDataId(null)
        setFeuillesData(prev => prev.filter(item => item.id !== deleteDataId))
      } catch (err) {
        // Error handled by AppContext
      }
    } else if (selectedItem.length > 0) {
      const selectedIds = selectedItem.map(item => item.id)
      try {
        const deleteData = { "code_feuille": feuilleId, "ids": selectedIds }
        await deleteFeuilleDataAPI(deleteData)
        setShowDeleteConfirm(false)
        setSelectedItem([])
        setFeuillesData(prev => prev.filter(item => !selectedIds.includes(item.id)))
      } catch (err) {
        // Error handled by AppContext
      }
    }
  }

  const handleValidateData = async () => {
    if (validateDataId) {
      try {
        const validateData = { "code_feuille": feuilleId, "ids": [validateDataId] }
        await validateFeuilleDataAPI(validateData)
        setFeuillesData(prev => prev.map(item => item.id == validateDataId ? { ...item, stat: 1 } : item))
        setShowValideConfirm(false)
        setValidateDataId(null)
      } catch (err) {
        // Error handled by AppContext
      }
    } else if (selectedItem.length > 0) {
      const selectedIds = selectedItem.map(item => item.id)
      try {
        const validateData = { "code_feuille": feuilleId, "ids": selectedIds }
        await validateFeuilleDataAPI(validateData)
        setFeuillesData(prev => prev.map(item => selectedIds.includes(item.id) ? { ...item, stat: 1 } : item))
        setShowValideConfirm(false)
        setSelectedItem([])
      } catch (err) {
        // Error handled by AppContext
      }
    }
  }

  // Formater les colonnes pour DataTable
  const columnsTable = [
    {
      name: 'N°',
      selector: (_, index) => index + 1,
      width: '50px',
    },
    ...feuilleColumns.map(col => ({
      name: col.nom_ligne,
      selector: row => row[col.nom_ligne] || '-',
      sortable: true,
      wrap: true,
    })),
    {
      name: 'Status',
      selector: row => row.stat == 0 ? <span className="badge bg-warning">En attente</span> : <span className="badge bg-success">Validé</span>,
      sortable: true,
      wrap: true,
    },
    {
      name: 'Actions',
      cell: row => (
        <div className="btn-group btn-group-sm">
          <Link to={`/feuille/${feuilleId}/data/${row.id}`}
            className="btn text-info d-flex align-items-center" title="Modifier">
            <BiPencil />
          </Link>
          <button className="btn text-danger d-flex align-items-center"
            onClick={() => { setdeleteDataId(row.id); setShowDeleteConfirm(true) }}
            title="Supprimer">
            <BiTrash />
          </button>
        </div>
      ),
      ignorerowclick: true,
      allowoverflow: true,
      button: "true",
    },
  ]

  // --- ANIMATIONS ---
  const tableVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
      },
    },
  }

  // --- EMPTY DATA ---
  if (feuilleColumns.length === 0) {
    return (
      <div className="text-center py-5 bg-light border">
        <p className="text-muted">
          Aucun champ défini.
          <br />
          Configurez d'abord les champs de cette feuille.
        </p>
      </div>
    )
  }

  return (
    <>
      <motion.div
        className="table-responsive p-0"
        variants={tableVariants}
        initial="hidden"
        animate="visible">
        <TableLayout
          title={`Données - ${currentFeuille?.nom_feuille}`}
          columns={columnsTable}
          data={feuilleData}
          setSelectedItem={setSelectedItem}
          setShowDeleteConfirm={setShowDeleteConfirm}
          setShowValideConfirm={setShowValideConfirm}
          loading={loading} />
      </motion.div>

      <ConfirmAlert
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteData}
        title="Supprimer la ligne de données"
        message={`Êtes-vous sûr de vouloir supprimer cette ligne de données ? Cette action est irréversible.`}
        confirmText="Supprimer"
        variant="danger"
      />

      <ConfirmAlert
        isOpen={showValideConfirm}
        onClose={() => setShowValideConfirm(false)}
        onConfirm={handleValidateData}
        title="Valider la ligne de données"
        message={`Êtes-vous sûr de vouloir valider cette ligne de données ? Cette action est irréversible.`}
        confirmText="Valider"
        variant="success"
      />
    </>
  )
}
